import axios from 'axios';
import { defineStore } from 'pinia';
import { getExpiration, startSessionTimer } from '../utils/helpers';
import { contractsStore } from '@/stores/contracts';
import { dashboardStore } from '@/stores/dashboard';
import { invoicesStore } from '@/stores/invoices';
import { noteOfInterestStore } from '@/stores/noteofinterests';
import { offerStore } from '@/stores/offers';
import { promptStore } from '@/stores/prompt';
import { subscriptionStore } from '@/stores/subscription';
import { userStore } from '@/stores/user';
import { workordersStore } from '@/stores/workorders';
import { publicIpv4 } from 'public-ip';

const getClientIp = async () =>
    await publicIpv4({
        fallbackUrls: ['https://ifconfig.co/ip'],
    });

export const authStore = defineStore('auth-store', {
    state: () => {
        return {
            auth_token: localStorage.getItem('auth_token'),
            expiration: localStorage.getItem('auth_token') ? getExpiration(localStorage.getItem('auth_token')) : '',
            accessUrl: '',
            scriveAccessUrl: '',
            logingIn: false,
            loginError: false,
            queueTimer: null,
            sessionTimer: localStorage.getItem('auth_token') ? startSessionTimer() : null,
            sessionHasEnded: false,
            loggedOut: false,
            passwordReset: null,
            authDetails: null,
        };
    },
    getters: {
        bankIdUrl(state) {
            return state.accessUrl;
        },
        scriveBankIdUrl(state) {
            return state.scriveAccessUrl;
        },
        isLogingIn(state) {
            return state.logingIn;
        },

        loggedInStatus(state) {
            return new Date() < new Date(state.expiration);
        },
        loggedInError(state) {
            return state.loginError;
        },

        expirationTime(state) {
            return state.expiration;
        },

        hasSessionEnded(state) {
            return state.sessionHasEnded;
        },

        updateStatus(state) {
            return state.passwordReset;
        },

        authConfig(state) {
            return {
                headers: { Authorization: `Bearer ${state.auth_token}` },
            };
        },

        authConfigPDF(state) {
            return {
                responseType: 'arraybuffer',
                headers: { Accept: 'application/pdf', Authorization: `Bearer ${state.auth_token}` },
            };
        },

        details(state) {
            return state.authDetails;
        },
    },

    actions: {
        async fetchBankIDUrl() {
            const clientIpAddress = await getClientIp();
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + 'bankid/authenticate', {
                loginType: 1,
                callbackUrl: location.origin + '/mina-sidor/bankid',
                endUserIp: clientIpAddress,
            });
            try {
                this.accessUrl = response.data.accessUrl;
            } catch (err) {
                this.accessUrl = '';
                console.error('Error loading BankIDUrl:', err);
                return err;
            }
        },
        // If there is a problem with new bank-id, reenable the scrive login. Remember to uncomment the gettransaction for scrive in mimer api and the button on the start page.
        // async scriveFetchBankIDUrl() {
        //   const clientIpAddress = await getClientIp();
        //   const response = await axios.post(process.env.VUE_APP_API_BASE_URL + 'bankid/scriveauthenticate', {
        //     loginType: 1,
        //     callbackUrl: location.origin + '/mina-sidor/bankid',
        //     endUserIp: clientIpAddress
        //   });
        //   try {
        //     this.scriveAccessUrl = response.data.accessUrl;
        //   } catch (err) {
        //     this.scriveAccessUrl = '';
        //     console.error('Error loading BankIDUrl:', err);
        //     return err;
        //   }
        // },
        async signInUser(userData) {
            this.loginError = false;
            this.logingIn = true;
            let response = null;

            try {
                const resp = await fetch(process.env.VUE_APP_API_BASE_URL + 'login', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(userData),
                });

                if (resp.ok) {
                    response = await resp.json();
                } else {
                    this.authDetails = resp.status;
                }
            } catch (err) {
                console.log(err);
            }

            try {
                if (response.accessToken) {
                    //Save the token to storage and state
                    localStorage.setItem('auth_token', response.accessToken);
                    this.auth_token = response.accessToken;
                    //Set the expiration
                    this.expiration = getExpiration(response.accessToken);
                    //Start sessionTimer
                    this.sessionTimer = this.sessionTimer || startSessionTimer();
                    //Login is done set the state for isLogingIn to false
                    this.logingIn = false;
                }

                //Redirect to the sync page
                if (response.applicantUpdateNeeded) {
                    this.$router.replace({ path: 'synkronisering' });
                }

                if (response.status === 404) {
                    this.loginError = true;
                    this.loggedIn = false;
                }
            } catch (err) {
                this.loginError = true;
                this.logingIn = false;
                console.error('Failed to login:', err);
                return err;
            }
        },

        async signInUserBankID(authData) {
            const response = await axios.post(process.env.VUE_APP_API_BASE_URL + 'bankid/login', authData);
            try {
                if (response.data.accessToken) {
                    //Save the token to storage and state
                    localStorage.setItem('auth_token', response.data.accessToken);
                    this.auth_token = response.data.accessToken;
                    //Set the expiration
                    this.expiration = getExpiration(response.data.accessToken);
                    //Start sessionTimer
                    this.sessionTimer = this.sessionTimer || startSessionTimer();
                    //Login is done set the state for isLogingIn to false
                    this.logingIn = false;
                    // Navigate to '/synkronisering' after successful login - delay is necessary as the previous routing to Bankid does not finish otherwise
                    if (response.applicantUpdateNeeded) {
                        setTimeout(() => {
                            this.$router.replace({ path: 'synkronisering' });
                        }, 1000);
                    }
                }

                if (response.status === 401) {
                    this.loggedIn = false;
                }
            } catch (err) {
                this.loginError = true;
                console.error('Failed to login:', err);
                return err;
            }
        },

        /**
         *
         */
        async queueAuthExtension() {
            //Create a queue if there is none
            if (!this.queueTimer) {
                //If the sessions exires in less than the queue time we fetch a new token ASAP otherwise five minutes
                let queueTime = new Date() > new Date(this.expiration - 300000) ? 0 : 300000;
                //Start the queue
                this.queueTimer = setTimeout(async () => {
                    const response = await axios.get(process.env.VUE_APP_API_BASE_URL + 'login/ping', this.authConfig);
                    try {
                        if (response.data.accessToken) {
                            //
                            localStorage.setItem('auth_token', response.data.accessToken);
                            //Set the expiration
                            this.expiration = getExpiration(response.data.accessToken);
                            //Start sessionTimer
                            this.sessionTimer = this.sessionTimer || startSessionTimer();
                            //Remove the queue
                            this.queueTimer = null;
                        }

                        if (response.status === 401) {
                            this.loggedIn = false;
                        }
                    } catch (err) {
                        this.loggedIn = false;
                        console.error('Failed to ping:', err);
                        return err;
                    }
                }, queueTime);
            }
        },

        /**
         * This method can be called to invalidate the current session.
         * @param userRequestedLogout, tells the metod if the user has iniated the logout or the session just ended.
         */
        async invalidateSession(userRequestedLogout = false) {
            //Cleanup old expirations
            this.loggedOut = false;
            this.sessionHasEnded = false;
            //Remove the token from memory and storage
            this.auth_token = '';
            localStorage.removeItem('auth_token');
            //Remove the session timer
            clearInterval(this.sessionTimer);
            this.sessionTimer = null;
            //Reset and remove queue timer
            clearTimeout(this.queueTimer);
            this.queueTimer = null;
            //Set expirationtime to a negativ value... just for fun
            this.expiration = -1;

            //Reset the stores
            contractsStore().$reset();
            dashboardStore().$reset();
            invoicesStore().$reset();
            noteOfInterestStore().$reset();
            offerStore().$reset();
            promptStore().$reset();
            subscriptionStore().$reset();
            userStore().$reset();
            workordersStore().$reset();

            //If the user requested the logout tell the view to  show the correct message.
            if (userRequestedLogout) {
                this.loggedOut = true;
            } else {
                this.sessionHasEnded = true;
            }
            //Route the user back to the start screen
            this.$router.replace({ path: '/' });
        },

        async resetPassword(user) {
            await axios.post(process.env.VUE_APP_API_BASE_URL + 'resetPassword', {
                civicNumber: user.replace('-', ''),
                urlPattern: window.location.origin + '/mina-sidor/glomt-losenord/?token={reset_token}',
            });
        },

        async updatePasseword(token, password) {
            const response = await axios.put(process.env.VUE_APP_API_BASE_URL + 'resetPassword/' + token, { password });
            try {
                if (response) {
                    this.passwordReset = true;
                }
            } catch (err) {
                this.passwordReset = false;
                console.error('Failed to reset the password:', err);
                return err;
            }
        },

        // If when logged in, the user doesn't have the "applicant" log out and show error, built for future extension.
        async invalidateUser(reason) {
            switch (reason) {
                case 'applicant':
                    this.invalidateSession(true);
                    this.authDetails = 'applicant';
                    this.loggedOut = false;
                    break;

                case 'bankIdapplicant':
                    this.invalidateSession(true);
                    this.authDetails = 'bankIdapplicant';
                    this.loggedOut = false;
                    break;

                default:
                    this.invalidateSession();
                    break;
            }
        },
    },
});
